import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const milestones = [
    { year: 'Step 1', title: t('about.step1Title'), icon: 'calendar_month', description: t('about.step1Text'), active: true },
    { year: 'Step 2', title: t('about.step2Title'), icon: 'mark_email_read', description: t('about.step2Text') },
    { year: 'Step 3', title: t('about.step3Title'), icon: 'door_open', description: t('about.step3Text') },
    { year: 'Step 4', title: t('about.step4Title'), icon: 'luggage', description: t('about.step4Text') },
  ];

  const faqs = [
    { question: t('about.faq1Q'), answer: t('about.faq1A') },
    { question: t('about.faq2Q'), answer: t('about.faq2A') },
    { question: t('about.faq3Q'), answer: t('about.faq3A') },
    { question: t('about.faq4Q'), answer: t('about.faq4A') },
    { question: t('about.faq5Q'), answer: t('about.faq5A') },
  ];

  return (
    <div className="bg-background-dark font-display text-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative w-full pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl overflow-hidden relative min-h-[400px] flex flex-col items-center justify-center text-center p-8 md:p-16 bg-cover bg-center" 
            style={{
              backgroundImage: `linear-gradient(rgba(24, 22, 17, 0.3) 0%, rgba(24, 22, 17, 0.7) 100%), url("https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1600&q=80")`
            }}
          >
            <div className="flex flex-col gap-4 max-w-3xl z-10">
              <span className="text-primary font-bold tracking-widest uppercase text-xs md:text-sm">{t('about.badge')}</span>
              <h1 className="text-white text-4xl md:text-6xl font-black leading-tight tracking-tighter">
                {t('about.heroTitle1')} <span className="text-primary">{t('about.heroTitle2')}</span>
              </h1>
              <p className="text-white/90 text-lg md:text-xl font-medium leading-relaxed max-w-2xl mx-auto">
                {t('about.heroSubtitle')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet Your Host Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col gap-6 order-2 lg:order-1"
            >
              <div className="flex items-center gap-2 text-primary mb-2">
                <span className="h-px w-8 bg-primary"></span>
                <span className="uppercase tracking-widest text-xs font-bold">{t('about.meetHostBadge')}</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold leading-tight text-white">
                {t('about.meetHostTitle')}
              </h2>
              <p className="text-white/60 text-lg leading-relaxed">
                {t('about.meetHostText1')}
              </p>
              <p className="text-white/60 text-lg leading-relaxed">
                {t('about.meetHostText2')}
              </p>
              <div className="pt-4">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full overflow-hidden border-2 border-primary bg-primary/20 flex items-center justify-center">
                    <span className="material-symbols-outlined text-primary text-2xl">person</span>
                  </div>
                  <div>
                    <p className="text-white font-bold">Marko</p>
                    <p className="text-primary text-sm">{t('about.yourHost')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative order-1 lg:order-2"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img 
                  alt="Marko - Your Host" 
                  className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" 
                  src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&q=80"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-16 bg-surface-dark/30 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">{t('about.whatYouGetTitle')}</h2>
            <p className="text-white/50">{t('about.whatYouGetSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="group p-8 rounded-xl bg-surface-dark border border-white/10 hover:border-primary/50 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform">
                <span className="material-symbols-outlined text-3xl">home</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('about.premiumComfort')}</h3>
              <p className="text-white/60 leading-relaxed">{t('about.premiumComfortText')}</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="group p-8 rounded-xl bg-surface-dark border border-white/10 hover:border-primary/50 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform">
                <span className="material-symbols-outlined text-3xl">location_on</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('about.primeLocations')}</h3>
              <p className="text-white/60 leading-relaxed">{t('about.primeLocationsText')}</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="group p-8 rounded-xl bg-surface-dark border border-white/10 hover:border-primary/50 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform">
                <span className="material-symbols-outlined text-3xl">support_agent</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('about.personalSupport')}</h3>
              <p className="text-white/60 leading-relaxed">{t('about.personalSupportText')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-16 text-center">
            <span className="text-primary font-bold tracking-widest uppercase text-xs mb-2">{t('about.howItWorksTitle')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">{t('about.howItWorksSubtitle')}</h2>
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-[19px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-white/10 to-transparent"></div>
            <div className="flex flex-col gap-12">
              {milestones.map((milestone, index) => (
                <motion.div 
                  key={milestone.year}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex gap-8 group"
                >
                  <div className="relative z-10 flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full bg-surface-dark border-2 ${milestone.active ? 'border-primary' : 'border-white/10'} flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-colors`}>
                      <span className="material-symbols-outlined text-white text-sm group-hover:text-black">{milestone.icon}</span>
                    </div>
                  </div>
                  <div className="flex-1 pt-2">
                    <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                      <span className="text-primary font-bold text-lg">{milestone.year}</span>
                      <h3 className="text-xl font-bold text-white">{milestone.title}</h3>
                    </div>
                    <p className="text-white/50 max-w-xl">{milestone.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-background-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-12 text-center">
            <span className="text-primary font-bold tracking-widest uppercase text-xs mb-2">{t('about.faqTitle')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">{t('about.faqTitle')}</h2>
            <p className="text-white/50 mt-4 max-w-2xl">{t('about.faqSubtitle')}</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={`bg-surface-dark border rounded-xl overflow-hidden transition-all duration-300 ${openFaq === index ? 'border-primary/40' : 'border-white/10'}`}
              >
                <button 
                  className="flex justify-between items-center cursor-pointer p-6 hover:bg-white/5 transition-colors w-full text-left"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <h3 className="text-lg font-bold text-white pr-6 hover:text-primary transition-colors">{faq.question}</h3>
                  <span className={`material-symbols-outlined text-primary transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>expand_more</span>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 text-white/60 leading-relaxed border-t border-white/5 pt-4">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
